/**
 * Paper fallback for one inspection (iter303).
 *
 * Export a fillable PDF (pre-filled with the job details), e-mail it to a
 * tech who can't use the app, then import the returned file straight
 * back onto this inspection.
 */
import { useRef, useState } from "react";
import { toast } from "sonner";
import { FileDown, Upload, FileText, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { downloadPdfUrl } from "@/lib/downloadBlob";
import { importInspectionPdf } from "@/lib/api";

export function InspectionFallbackDialog({ inspection, onImported, onClose }) {
  const locations = inspection.locations || [];
  const [choice, setChoice] = useState(locations.length ? "locations" : "blank");
  const [busy, setBusy] = useState(false);
  const [markComplete, setMarkComplete] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const fileRef = useRef(null);
  const imports = inspection.pdf_imports || [];

  const download = async () => {
    const q = choice === "blank"
      ? "mode=blank"
      : choice === "locations"
        ? "mode=locations"
        : `location_id=${encodeURIComponent(choice)}`;
    await downloadPdfUrl(`/inspections/${inspection.id}/fillable-pdf?${q}`, {
      suggestedName: `${inspection.wo_number || "WO"}-${(inspection.template_name || "inspection").replace(/\s+/g, "-")}-fillable.pdf`,
      label: "fillable form",
    });
  };

  const doImport = async (file) => {
    if (!file) return;
    setBusy(true);
    setResult(null);
    setError("");
    try {
      const { data } = await importInspectionPdf(inspection.id, file, markComplete);
      setResult(data.imported);
      toast.success(`${data.imported.answers} answer${data.imported.answers === 1 ? "" : "s"} imported`
        + (data.imported.flagged ? ` · ${data.imported.flagged} flagged` : ""));
      onImported?.();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not import that PDF";
      setError(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="inspection-fallback-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-4 h-4 text-blue-500" />
            Paper fallback — {inspection.template_name}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-1">
          <p className="text-xs text-slate-500">
            For a tech who can't get into the app. The form comes out pre-filled with the customer,
            work order, site and address — they fill in the answers in any PDF reader, save, and
            e-mail it back. Photos can't travel inside the form; they come separately.
          </p>

          <div className="space-y-2 border border-slate-200 rounded-lg p-3">
            <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              1 · Export the form
            </Label>
            <Select value={choice} onValueChange={setChoice}>
              <SelectTrigger className="h-9 text-sm" data-testid="select-fallback-scope"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="blank">Blank form — tech names the location</SelectItem>
                {locations.length > 0 && (
                  <SelectItem value="locations">
                    One form per location ({locations.length}) — answers so far included
                  </SelectItem>
                )}
                {locations.map((l) => (
                  <SelectItem key={l.id} value={l.id}>
                    Just {l.identifier ? `${l.name} (${l.identifier})` : l.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button size="sm" className="w-full" onClick={download} data-testid="btn-download-fillable-pdf">
              <FileDown className="w-3.5 h-3.5 mr-1.5" />Download fillable PDF
            </Button>
          </div>

          <div className="space-y-2 border border-slate-200 rounded-lg p-3">
            <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              2 · Import it back
            </Label>
            <label className="flex items-start gap-2 text-xs cursor-pointer text-slate-600">
              <input
                type="checkbox" checked={markComplete} disabled={busy}
                onChange={(e) => setMarkComplete(e.target.checked)}
                className="mt-0.5 h-3.5 w-3.5 accent-slate-900"
                data-testid="toggle-fallback-mark-complete"
              />
              Mark the inspection complete after importing
            </label>
            <input
              ref={fileRef} type="file" accept="application/pdf" className="hidden"
              onChange={(e) => doImport(e.target.files?.[0])}
              data-testid="input-fallback-pdf-file"
            />
            <Button
              size="sm" variant="outline" className="w-full" disabled={busy}
              onClick={() => fileRef.current?.click()}
              data-testid="btn-import-fallback-pdf"
            >
              <Upload className="w-3.5 h-3.5 mr-1.5" />{busy ? "Reading the form…" : "Choose the returned PDF"}
            </Button>
            {error && (
              <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-2 py-1.5"
                   data-testid="fallback-import-error">
                {error}
              </div>
            )}
            {result && (
              <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-2 py-1.5"
                   data-testid="fallback-import-result">
                <CheckCircle2 className="w-3.5 h-3.5 inline mr-1" />
                {result.answers} answers, {result.notes} notes
                {result.flagged ? `, ${result.flagged} flagged` : ""}
                {result.unmatched ? `, ${result.unmatched} skipped (template changed)` : ""}
                {result.locations?.length ? ` → ${result.locations.join(", ")}` : ""}
              </div>
            )}
          </div>

          {imports.length > 0 && (
            <div className="text-[11px] text-slate-500 space-y-0.5" data-testid="fallback-import-log">
              <div className="font-semibold text-slate-400 uppercase tracking-wide">Previous imports</div>
              {imports.slice(-4).reverse().map((i, idx) => (
                <div key={idx}>
                  {new Date(i.at).toLocaleString()} — {i.answers} answers from {i.filename || "a PDF"} by {i.by}
                </div>
              ))}
            </div>
          )}
        </div>

        <DialogFooter className="mt-3">
          <Button variant="outline" size="sm" onClick={onClose} data-testid="btn-close-fallback">Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
